<template>
  <el-row v-loading.fullscreen.lock="fullScreenLoading">
    <el-col
      :sm="{ span: 14, offset: 5 }"
      :md="{ span: 12, offset: 6 }"
      :lg="{ span: 12, offset: 6 }"
    >
      <el-header
        height="320px"
        class="app-header pt-50 pb-30"
      >
        <Menu />
        <h3 class="app-header-title mt-0">
          {{ $t("profile.my-profile") }}
        </h3>
        <div
          v-if="userProfile.photo"
          class="app-header-avatar-image"
          :style="{
            'background-image': `url(https://tippiie-images.s3.eu-central-1.amazonaws.com/public/${
              userProfile.photo
            }?t=${timestamp})`,
          }"
        />
        <h2 class="app-header-name mt-10 mb-10">
          {{ userProfile.name }} {{ userProfile.surName }}
        </h2>
        <div class="app-header-avatar-uploader">
          <el-button
            type="default"
            class="upload-button"
            @click="toggleShow"
          >
            {{ $t("profile.change-image") }}
          </el-button>
          <crop-upload
            :key="$i18n.locale"
            @crop-success="cropSuccess"
            :model-value.sync="showCrop"
            :width="400"
            :height="400"
            :no-square="true"
            :no-rotate="true"
            :lang-type="$i18n.locale"
          />
        </div>
      </el-header>
      <el-main v-if="!isLoading">
        <el-tabs type="card">
          <el-tab-pane :label="$t('profile.balance')">
            <Balance
              :balances="balances"
              :has-iban="userProfile.hasIban"
              :roles="roles"
            />
          </el-tab-pane>
          <el-tab-pane :label="$t('profile.title')">
            <Profile :user="userProfile" />
          </el-tab-pane>
          <el-tab-pane :label="$t('profile.assignments')">
            <Assignments />
          </el-tab-pane>
        </el-tabs>
      </el-main>
    </el-col>
  </el-row>
</template>

<script>
import Menu from "../Menu/Menu";
import Profile from "./Tabs/Profile";
import Assignments from "./Tabs/Assignments";
import Balance from "./Tabs/Balance";
import { Auth } from '@aws-amplify/auth';
import { Storage } from '@aws-amplify/storage';
import cropUpload from "vue-image-crop-upload";

export default {
  name: "Dashboard",
  components: {
    Menu,
    Profile,
    Assignments,
    Balance,
    cropUpload,
  },
  data: () => ({
    timestamp: new Date().getTime(),
    isLoading: true,
    fullScreenLoading: true,
    balances: [],
    userProfile: {},
    showCrop: false,
    roles: [],
  }),
  async created() {
    await this.getCurrentUser()
      .then(async (user) => {
        this.userProfile.name = user.attributes.name;
        this.userProfile.surName = user.attributes.family_name;
        this.userProfile.phoneNum = user.attributes.phone_number;
        this.userProfile.email = user.attributes.email || "";
        this.userProfile.iban = user.attributes["custom:iban"] || "";
        this.userProfile.hasIban = !!(user.attributes['custom:iban'] && user.attributes['custom:iban'].length)
        this.userProfile.ibanStatus =
          user.attributes["custom:iban_status"] || false;
        this.userProfile.photo = user.attributes.picture || "profile.png";
        this.userProfile.sub = user.attributes.sub;
        await Auth.currentAuthenticatedUser({
          bypassCache: true,
        }).then((user) =>  this.roles = user.signInUserSession.accessToken.payload["cognito:groups"] || []);
        await this.$adminApi
          .get(`/api/v1/employees/${user.attributes.sub}/balance`)
          .then((response) => {
            const balances = response.data || [];
            if (Array.isArray(balances) && balances.length) {
              this.balances = balances;
            } else if (
              Object.keys(balances).length !== 0 &&
              balances.constructor === Object
            ) {
              this.balances = [balances];
            }
          });
      })
      .catch(() => {
        this.fullScreenLoading = false;
      })
      .finally(() => {
        this.fullScreenLoading = false;
        this.isLoading = false;
      });
  },
  methods: {
    handleAvatarChange(file) {
      this.fullScreenLoading = true;
      const isCorrectType =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/gif";
      const sizeLimit = file.size / 1024 / 1024 < 50;

      if (!isCorrectType) {
        this.$message.error(
          this.$t("form.validation.avatar-format", { format: "JPEG, PNG" })
        );
        return false;
      }
      if (!sizeLimit) {
        this.$message.error(
          this.$t("form.validation.avatar-size", { size: "50MB" })
        );
        return false;
      }
      this.uploadProfileAvatar(file);
    },
    uploadProfileAvatar(file) {
      this.getCurrentUser()
        .then((user) => {
          new Promise((resolve, reject) => {
            if (user.attributes.picture) {
              Storage.remove(user.attributes.picture)
                .then(() => resolve())
                .catch(() => reject());
            } else {
              resolve();
            }
          })
            .then(() => {
              Storage.put(file.name, file, { level: "public" })
                .then((response) => {
                  this.userProfile.photo = response.key;
                  Auth.updateUserAttributes(user, {
                    picture: response.key,
                  })
                    .then(() => {
                      this.$adminApi
                        .patch("/api/v1/employees/" + user.attributes.sub, {
                          email: this.userProfile.email,
                          phoneNumber: this.userProfile.phoneNum,
                          name: this.userProfile.name,
                          familyName: this.userProfile.surName,
                        })
                        .then((response) => {
                          this.fullScreenLoading = false;
                          if (response.data.status === "Success") {
                            this.timestamp = new Date().getTime()
                            this.$message.success(
                              this.$t("form.item-changed", { item: "Avatar" })
                            );
                          } else {
                            this.$message.warning(response.data.status);
                          }
                        })
                        .catch(() => {
                          this.fullScreenLoading = false;
                          this.$message.error(
                            this.$t("form.item-not-changed", { field: "logo" })
                          );
                        });
                    })
                    .catch((error) => {
                      this.fullScreenLoading = false;
                      this.$message.error(error.message);
                    });
                })
                .catch(() => {
                  this.fullScreenLoading = false;
                  this.$message.error(
                    this.$t("form.item-not-changed", { field: "logo" })
                  );
                });
            })
            .catch(() => {
              this.fullScreenLoading = false;
              this.$message.error(
                this.$t("form.item-not-changed", { field: "logo" })
              );
            });
        })
        .catch(() => {
          this.$router.push({ name: "SignIn" });
        });
    },
    toggleShow() {
      this.showCrop = !this.showCrop;
    },
    cropSuccess(imgDataUrl) {
      const ext = imgDataUrl.substring(
        "data:image/".length,
        imgDataUrl.indexOf(";base64")
      );
      const file = this.dataURLtoFile(
        imgDataUrl,
        `${this.userProfile.sub}.${ext}`
      );
      this.handleAvatarChange(file);
    },
    dataURLtoFile(dataUrl, filename) {
      let arr = dataUrl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bst = atob(arr[1]),
        n = bst.length,
        u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bst.charCodeAt(n);
      }

      return new File([u8arr], filename, { type: mime });
    },
  },
};
</script>

<style scoped lang="scss">
.vue-image-crop-upload::v-deep {
  .vicp-wrap {
    @media (max-width: 600px) {
      width: 100%;
      height: auto;
      padding: 40px 5px;
    }

    .vicp-img-container {
      @media (max-width: 600px) {
        margin: 0 auto;
      }
    }

    .vicp-crop-right {
      @media (max-width: 600px) {
        margin: 0 auto;
      }
    }

    .vicp-range {
      @media (max-width: 600px) {
        margin: 30px auto !important;
      }
    }

    .vicp-crop {
      @media (max-width: 600px) {
        display: flex;
        flex-direction: column;
      }
    }
  }
}

.el-tabs {
  padding-bottom: 15px;
}
</style>
